import React from 'react'
import { Button, Stack, useMediaQuery, useTheme } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { ButtonWithLoading } from '@e3dc-react/shell/Elements/Buttons'
import { makeStyles } from 'tss-react/mui'
import { isNil } from 'lodash'
import { useFormContext } from 'react-hook-form'

interface StyleProps {
  isLeftControlsActive: boolean
}
const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  leftControls: {
    margin: theme.spacing(2, 0),
    [theme.breakpoints.up('md')]: {
      margin: 0,
      marginRight: 'auto',
    },
  },
  rightButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    [theme.breakpoints.up('md')]: {
      marginLeft: 'auto',
    },
    gap: theme.spacing(2),
  },
  stackContainer: {
    flexDirection: props.isLeftControlsActive ? 'row' : 'column',
    justifyContent: 'space-between',
    [theme.breakpoints.down('md')]: {
      flexDirection: props.isLeftControlsActive ? 'row' : 'row-reverse',
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
    padding: theme.spacing(2, 4),
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
  },
}))

export interface FormProps {
  isLoading?: boolean
  leftControls?: React.ReactNode
  children?: React.ReactNode
  onReset: () => void
}

/**
 * Container for form with save and reset button, with an option to add a left control
 */
const SettingsFormContainer: React.FC<FormProps> = ({ children, isLoading, leftControls, onReset }) => {
  const isLeftControlsActive = !isNil(leftControls)
  const { classes } = useStyles({ isLeftControlsActive })
  const {
    formState: { isDirty, isSubmitting },
  } = useFormContext()
  const { breakpoints } = useTheme()

  const submitIsDisabled = !isDirty || isSubmitting

  const { t } = useCustomTranslation()
  const fullWidthButtons = useMediaQuery(breakpoints.down('md'))

  const handleReset = (): void => {
    onReset()
  }

  return (
    <>
      <Stack sx={{ p: 4, pb: 2, pt: 2 }}>{children}</Stack>
      <Stack className={classes.stackContainer}>
        {leftControls && <Stack className={classes.leftControls}>{leftControls}</Stack>}
        <Stack className={classes.rightButtons}>
          <Button
            type="button"
            variant="outlined"
            size="large"
            color="inherit"
            disabled={!isDirty}
            onClick={handleReset}
            fullWidth={fullWidthButtons}
            sx={{ width: '100%', minWidth: 'auto' }}
          >
            {t('common.reset')}
          </Button>
          <ButtonWithLoading
            fullWidth={fullWidthButtons}
            size="large"
            color="primary"
            type="submit"
            disabled={submitIsDisabled}
            isLoading={isLoading}
            label={t('common.save')}
            buttonProps={{ type: 'submit' }}
          />
        </Stack>
      </Stack>
    </>
  )
}

export default SettingsFormContainer
